
import React from 'react';
import { Link } from 'react-router-dom';
import PageWrapper from '@/components/utils/PageWrapper';
import AnimatedElement from '@/components/utils/AnimatedElement';
import { Brain, ArrowLeft, ChevronRight } from 'lucide-react';
import AnimatedLink from '@/components/ui/animated-link';
import { motion } from 'framer-motion';

const MachineLearningPage = () => {
  return (
    <PageWrapper>
      <main className="flex-grow pt-32 pb-20">
        <div className="container mx-auto px-6">
          <div className="mb-6">
            <Link to="/services" className="text-muted-foreground hover:text-primary flex items-center transition-colors">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to all services
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-12 mb-16">
            <div className="md:col-span-2">
              <AnimatedElement animation="fadeIn">
                <div className="bg-purple-50 text-purple-600 dark:bg-purple-950 dark:text-purple-400 p-3 rounded-xl inline-block mb-4">
                  <Brain className="h-10 w-10" />
                </div>
                <h1 className="text-4xl md:text-5xl font-bold mt-4 mb-6">
                  Machine <span className="text-primary">Learning</span>
                </h1>
                <p className="text-xl text-muted-foreground mb-6">
                  Custom ML models that learn from your data to make predictions and automate decisions.
                </p>
              </AnimatedElement>

              <AnimatedElement animation="slideUp" delay={0.2}>
                <div className="prose prose-lg dark:prose-invert max-w-none">
                  <p>
                    Machine learning enables computers to learn and improve from experience without being explicitly programmed. Our machine learning solutions help businesses automate processes, gain insights, and make predictions that drive growth and innovation.
                  </p>
                  
                  <h2>Our Approach</h2>
                  <p>
                    We develop custom machine learning models tailored to your specific business needs. Our iterative development process ensures that models continually improve over time, providing increasingly accurate and valuable insights.
                  </p>
                  
                  <h2>Key Benefits</h2>
                  <ul>
                    <li>Automate complex decision-making processes</li>
                    <li>Discover hidden patterns and relationships in your data</li>
                    <li>Predict future trends and outcomes with greater accuracy</li>
                    <li>Enhance customer experiences through personalization</li>
                    <li>Reduce operational costs through automation</li>
                    <li>Gain competitive advantage through data-driven insights</li>
                  </ul>
                </div>
              </AnimatedElement>
            </div>

            <div className="md:col-span-1">
              <motion.div 
                className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 shadow-subtle border border-gray-100 dark:border-gray-700 sticky top-32"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300, damping: 15 }}
              >
                <h3 className="text-xl font-semibold mb-4">Our Machine Learning Services</h3>
                <ul className="space-y-3">
                  {[
                    "Supervised Learning",
                    "Unsupervised Learning",
                    "Deep Learning",
                    "Natural Language Processing",
                    "Computer Vision",
                    "Reinforcement Learning"
                  ].map((service, index) => (
                    <motion.li 
                      key={index}
                      className="flex items-start"
                      whileHover={{ x: 5 }}
                      transition={{ type: "spring", stiffness: 400, damping: 10 }}
                    >
                      <ChevronRight className="mr-2 h-5 w-5 text-primary flex-shrink-0" />
                      <span>{service}</span>
                    </motion.li>
                  ))}
                </ul>

                <div className="mt-8">
                  <AnimatedLink to="/contact" fullWidth>
                    Contact Us
                  </AnimatedLink>
                </div>
              </motion.div>
            </div>
          </div>

          <motion.div 
            className="bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-950/30 dark:to-blue-950/30 p-8 rounded-2xl mb-16"
            whileHover={{ scale: 1.02 }}
            transition={{ type: "spring", stiffness: 300, damping: 15 }}
          >
            <h2 className="text-2xl font-semibold mb-4">Ready to implement machine learning solutions?</h2>
            <p className="text-muted-foreground mb-6">
              Let's discuss how our machine learning services can transform your business operations and drive innovation.
            </p>
            <AnimatedLink to="/contact">
              Get Started
            </AnimatedLink>
          </motion.div>
        </div>
      </main>
    </PageWrapper>
  );
};

export default MachineLearningPage;
